// src/shared/components/ui/Button.tsx

import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import './Button.css';

// Tipos para las variantes del botón
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'warning';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Variante visual del botón */
  variant?: ButtonVariant;
  /** Tamaño del botón */
  size?: ButtonSize;
  /** Si el botón está en estado de carga */
  loading?: boolean;
  /** Texto que se muestra cuando está cargando */
  loadingText?: string;
  /** Icono que se muestra antes del texto */
  leftIcon?: ReactNode;
  /** Icono que se muestra después del texto */
  rightIcon?: ReactNode;
  /** Si el botón debe ocupar todo el ancho disponible */
  fullWidth?: boolean;
  /** Contenido del botón */
  children: ReactNode;
  /** Clase CSS adicional */
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingText = 'Cargando...',
  leftIcon,
  rightIcon,
  fullWidth = false,
  children,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = 'btn';
  const variantClass = `btn--${variant}`;
  const sizeClass = `btn--${size}`;
  const fullWidthClass = fullWidth ? 'btn--full-width' : '';
  const loadingClass = loading ? 'btn--loading' : '';
  const disabledClass = (disabled || loading) ? 'btn--disabled' : '';

  const buttonClasses = [
    baseClasses,
    variantClass,
    sizeClass,
    fullWidthClass,
    loadingClass,
    disabledClass,
    className
  ].filter(Boolean).join(' ');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) {
      event.preventDefault();
      return;
    }
    props.onClick?.(event);
  };

  return (
    <button
      {...props}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={handleClick}
    >
      {/* Spinner de carga */}
      {loading && (
        <span className="btn__spinner" aria-hidden="true">
          <svg className="btn__spinner-icon" viewBox="0 0 24 24">
            <circle
              className="btn__spinner-circle"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeDasharray="31.416"
              strokeDashoffset="31.416"
            />
          </svg>
        </span>
      )}

      {/* Contenido del botón */}
      <span className={`btn__content ${loading ? 'btn__content--hidden' : ''}`}>
        {leftIcon && <span className="btn__icon btn__icon--left">{leftIcon}</span>}
        <span className="btn__text">
          {loading ? loadingText : children}
        </span>
        {rightIcon && <span className="btn__icon btn__icon--right">{rightIcon}</span>}
      </span>
    </button>
  );
};

export default Button;